import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader2 } from 'lucide-react';
import { createCheckoutSession, fetchStripeProduct, type StripeProduct } from '../services/api';
import { CREDITS_PACK_PRODUCT_ID } from '../config/systemConfig';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<StripeProduct | null>(null);
  const [fetchingProduct, setFetchingProduct] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFetchingProduct(true);
      fetchStripeProduct(CREDITS_PACK_PRODUCT_ID)
        .then(setProduct)
        .catch(err => console.error("Failed to fetch product", err))
        .finally(() => setFetchingProduct(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBuyCredits = async () => {
    setLoading(true);
    setError(null);
    try {
      const amountCents = product?.price?.unit_amount || 399;
      const currency = product?.price?.currency || 'USD';
      const productName = product?.name || 'Credits Pack (20 Credits)';
      
      const { url } = await createCheckoutSession(
        amountCents, 
        currency, 
        productName, 
        '/credits/success',
        product?.price?.id,
        CREDITS_PACK_PRODUCT_ID
      );
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("Failed to start checkout");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to start checkout");
      setLoading(false);
    }
  };

  const displayPrice = product?.price 
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: product.price.currency }).format(product.price.unit_amount / 100)
    : '$3.99';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in duration-200">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-blue-600">20</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Get More Credits</h2>
          <p className="text-gray-500 mt-1">Generate 20 more images with AI</p>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-gray-700">Credits Pack</span>
            {fetchingProduct ? (
              <div className="h-6 w-16 bg-gray-200 animate-pulse rounded"></div>
            ) : (
              <span className="font-bold text-gray-900">{displayPrice}</span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            One-time purchase. No subscription.
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 text-sm text-left">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={handleBuyCredits}
          disabled={loading || fetchingProduct}
          className="w-full bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? 'Processing...' : `Purchase for ${displayPrice}`}
        </button>
        
        <p className="text-xs text-center text-gray-400 mt-4">
          Secured by Stripe
        </p>
      </div>
    </div>
  );
};
